/* eslint-disable complexity */
import { useState, useRef, ReactNode } from 'react';

import styled from '@emotion/styled';
import { colors, hasFullAddressChanged, FullAddress } from '@modernanimal/common';
import { validNameCharacters, validateName } from '@modernanimal/common/src/utils/clientOnboarding';
import Modal from '@modernanimal/common/src/web/atoms/Modal';
import PlacesInputField, { PlacesInputFieldRefType } from '@modernanimal/common/src/web/PlacesInputField';
import * as Sentry from '@sentry/react';
import { validate } from 'email-validator';
import posthog from 'posthog-js';
import { useBreakpoint } from 'use-breakpoint';

import { patchClient, isClientEdited } from './clientOnboarding.helpers';
import { CustomerMembershipTierEnum, ClientInputPartial } from '../__generated__/graphql';
import InputField from '../components/InputField';
import LoadingModal from '../components/LoadingModal';
import PrimaryButton from '../components/PrimaryButton';
import Fonts from '../fonts';
import {
    BREAKPOINTS, trackGTMPurchaseEvent, pushToGTM, GTM_EVENT_CLIENT_ACCOUNT_CREATED
} from '../helpers';
import Icons from '../icons';
import { createPatientRelatedEntities } from '../patientOnboarding/patientOnboarding.helpers';
import useWebBookingStore, { ClientStoreType } from '../store';

type Props = {
    locationShowCreditCardStep: boolean;
    goToNextStep: ()=> void;
    locationId?: string | null;
};

type NameVariant = 'first' | 'last';

const ClientOnboarding = ({ locationShowCreditCardStep, goToNextStep, locationId }: Props) => {
    const {
        client, updateClient, patient, updatePatient, externalClinic, isMedicalRecordRequested,
        updateIsMedicalRecordRequested, appointment, isMembershipCreated, updateIsMembershipCreated,
        updateAppointment
    } = useWebBookingStore((state) => ({
        client: state.client,
        patient: state.patient,
        updateClient: state.setClient,
        updatePatient: state.setPatient,
        externalClinic: state.externalClinic,
        isMedicalRecordRequested: state.isMedicalRecordRequested,
        updateIsMedicalRecordRequested: state.setIsMedialRecordsRequested,
        appointment: state.appointment,
        isMembershipCreated: state.isMembershipCreated,
        updateIsMembershipCreated: state.setIsMembershipCreated,
        updateAppointment: state.setAppointment
    }));

    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';

    const [firstName, setFirstName] = useState<string>(client.firstName);
    const [lastName, setLastName] = useState<string>(client.lastName);
    const [email, setEmail] = useState<string>(client.email);
    const [fullAddress, setFullAddress] = useState<FullAddress>(client.fullAddress);
    const [selectedAddress, setSelectedAddress] = useState<Pick<google.maps.places.PlacePrediction, 'text' | 'placeId'> | null>(
        (client.fullAddress.text && client.fullAddress.placeId)
            ? { text: client.fullAddress.text, placeId: client.fullAddress.placeId }
            : null
    );

    const [firstNameIsValid, setFirstNameIsValid] = useState<boolean | null>(client.firstName ? true : null);
    const [lastNameIsValid, setLastNameIsValid] = useState<boolean | null>(client.lastName ? true : null);
    const [emailIsValid, setEmailIsValid] = useState<boolean | null>(client.email ? true : null);
    const [addressIsValid, setAddressIsValid] = useState<boolean | null>(client.fullAddress.address ? true : null);

    const [isWhyInfoModalOpen, setIsWhyInfoModalOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const placesInputFieldRef = useRef<PlacesInputFieldRefType>();

    const changeFirstName = (name: string) => {
        if (validNameCharacters(name)) setFirstName(name);
        if (name.length >= 2) setFirstNameIsValid(true);
        if (firstNameIsValid && name.length < 2) setFirstNameIsValid(null);
    };

    const changeLastName = (name: string) => {
        if (validNameCharacters(name)) setLastName(name);
        if (name.length >= 2) setLastNameIsValid(true);
        if (lastNameIsValid && name.length < 2) setLastNameIsValid(null);
    };

    const changeName = (variant: NameVariant) => (name: string) => {
        if (variant === 'first') {
            changeFirstName(name);
        } else if (variant === 'last') {
            changeLastName(name);
        }
    };

    const changeEmail = () => (email: string) => {
        const trimmedEmail = email.trim();

        setEmail(trimmedEmail.toLowerCase());
        if (validate(trimmedEmail.toLowerCase())) setEmailIsValid(true);
        if (emailIsValid && !validate(trimmedEmail.toLowerCase())) setEmailIsValid(null);
    };

    const validateNames = (variant: NameVariant) => {
        if (variant === 'first') {
            const isValidFirstName = !!validateName(firstName);
            setFirstNameIsValid(isValidFirstName);
        } else if (variant === 'last') {
            const isValidLastName = !!validateName(lastName);
            setLastNameIsValid(isValidLastName);
        }
    };

    const handleNext = async () => {
        pushToGTM({ event: GTM_EVENT_CLIENT_ACCOUNT_CREATED });
        setLoading(true);

        const updatedClient: ClientStoreType = {
            ...client,
            firstName,
            lastName,
            email,
            household: {
                id: client.household?.id,
                signupLocationId: client.household?.signupLocationId,
                timezone: client.household?.timezone
            },
            fullAddress: {
                ...fullAddress,
                text: selectedAddress?.text || { matches: [], text: '' },
                placeId: selectedAddress?.placeId || ''
            }
        };
        const isEdited = isClientEdited(client, updatedClient);

        let isPatched = false;
        if (isEdited) {
            try {
                const patchedClientInput: ClientInputPartial = {
                    id: client.id,
                    firstName,
                    lastName,
                    email,
                    address: fullAddress.address,
                    apartment: fullAddress.apartment,
                    city: fullAddress.city,
                    state: fullAddress.state,
                    zipcode: fullAddress.zipcode
                };
                const patchedClient = await patchClient(client.token, patchedClientInput);
                isPatched = !!patchedClient;
            } catch (error) {
                // eslint-disable-next-line no-alert
                alert(error);
                Sentry.captureException(error, {
                    extra: {
                        clientId: client.id,
                        tokenPresent: !!client.token,
                        function: 'handleNext',
                        path: 'src/clientOnboarding/ClientOnboarding'
                    },
                });
                setLoading(false);
                return null;
            }
        }

        if (!locationShowCreditCardStep) {
            const createdAppointmentId = await createPatientRelatedEntities(
                patient,
                client,
                externalClinic,
                isMedicalRecordRequested,
                isMembershipCreated,
                appointment,
                updatePatient,
                updateIsMedicalRecordRequested,
                updateIsMembershipCreated,
                updateAppointment,
                locationId as string,
            );
            if (!createdAppointmentId) {
                return setLoading(false);
            }
            trackGTMPurchaseEvent(createdAppointmentId, CustomerMembershipTierEnum.PayAsYouGo);
        }

        setLoading(false);

        if (!isEdited || isPatched) {
            const userProperties = {
                'email': updatedClient.email || undefined,
                'City': updatedClient?.fullAddress?.city || undefined,
                'First Name': updatedClient.firstName || undefined,
                'Last Name': updatedClient.lastName || undefined,
                'Phone Number': updatedClient.phoneNumber || undefined,
                'Signup Location ID': appointment.slot?.location.id || locationId || undefined,
            };

            // Update actual user properties in PostHog
            posthog.people.set(userProperties);
            // Also update properties for feature flags
            posthog.setPersonPropertiesForFlags(userProperties, true);

            updateClient(updatedClient);
            goToNextStep();
        }

        return null;
    };

    const selectAddress = async (prediction: Pick<google.maps.places.PlacePrediction, 'text' | 'placeId'>) => {
        setSelectedAddress(prediction);
        await placesInputFieldRef.current?.formatAddress(
            prediction,
            fullAddress.apartment,
            (formattedAddress) => {
                setFullAddress(formattedAddress);
                setAddressIsValid(true);
            },
            () => setAddressIsValid(false)
        );
    };

    const renderClientInputBody = () => {
        const SECTIONS: { title: string, input: ReactNode }[] = [{
            title: 'Full Name',
            input: (
                <>
                    <InputField
                        placeholder="First name"
                        value={firstName}
                        onChange={changeName('first')}
                        onBlur={() => firstName && validateNames('first')}
                        autoFocus={!firstName}
                        isInvalid={firstNameIsValid === false}
                    />
                    {firstNameIsValid === false && (
                        <Fonts.BodySmall
                            style={{ color: colors.RED }}
                        >
                            Your name must be 2 or more characters

                        </Fonts.BodySmall>
                    )}
                    <InputField
                        placeholder="Last name"
                        value={lastName}
                        onChange={changeName('last')}
                        onBlur={() => lastName && validateNames('last')}
                        isInvalid={lastNameIsValid === false}
                    />
                    {lastNameIsValid === false && (
                        <Fonts.BodySmall
                            style={{ color: colors.RED }}
                        >
                            Your name must be 2 or more characters

                        </Fonts.BodySmall>
                    )}
                </>
            )
        }, {
            title: 'Email Address',
            input: (
                <>
                    <InputField
                        placeholder="Email address"
                        value={email}
                        onChange={changeEmail()}
                        onBlur={() => email && setEmailIsValid(validate(email))}
                        isInvalid={emailIsValid === false}
                        inputMode="email"
                    />
                    {emailIsValid === false && (
                        <Fonts.BodySmall style={{ color: colors.RED }}>
                            Please enter a valid email address
                        </Fonts.BodySmall>
                    )}
                </>

            )
        }, {
            title: 'Address',
            input: (
                <>
                    <PlacesInputField
                        ref={placesInputFieldRef}
                        searchType="autocomplete"
                        placeholder="Address"
                        hasChanged={hasFullAddressChanged(client.fullAddress, fullAddress)}
                        showError={addressIsValid === false}
                        selectedValue={selectedAddress}
                        onSelectResult={selectAddress}
                        types={['street_address']}
                    />
                    <InputField
                        placeholder="Apartment number"
                        value={fullAddress.apartment}
                        onChange={(apartment) => setFullAddress({ ...fullAddress, apartment })}
                    />
                </>
            )
        }];
        return (
            <Body>
                <Fonts.Heading04>{"Let's complete your booking"}</Fonts.Heading04>
                <ClientInputContainer>
                    {SECTIONS.map((section) => (
                        <SectionContainer key={section.title}>
                            <Fonts.LabelMedium>{section.title}</Fonts.LabelMedium>
                            {section.input}
                        </SectionContainer>
                    ))}
                    <button
                        type="button"
                        onClick={() => setIsWhyInfoModalOpen(true)}
                        style={{ textAlign: 'left' }}
                    >
                        <Fonts.Body style={{ color: colors.PRIMARY_PERIWINKLE, textDecoration: 'underline' }}>
                            Why do we need this information?
                        </Fonts.Body>
                    </button>
                </ClientInputContainer>
            </Body>
        );
    };

    const renderClientInputButton = () => (
        <NextButtonContainer isMobile={isMobile}>
            <PrimaryButton
                id="client-onboarding-button"
                color="periwinkle"
                loading={loading}
                disabled={!firstNameIsValid || !lastNameIsValid || !emailIsValid || !addressIsValid}
                onClick={handleNext}
            >
                {locationShowCreditCardStep ? 'Next' : 'Confirm Booking'}
            </PrimaryButton>
        </NextButtonContainer>
    );

    const renderWhyInfoModal = () => (
        <Modal
            isModalOpen={isWhyInfoModalOpen}
            onCloseModal={() => setIsWhyInfoModalOpen(false)}
            shouldCloseOnOverlayClick
        >
            <ModalContainer>
                <Icons.WhyInfoDog height={91.73} width={120} />
                <ModalExitContainer onClick={() => setIsWhyInfoModalOpen(false)}>
                    <Icons.CircleGreyX height={24} width={24} />
                </ModalExitContainer>
                <Fonts.Heading06>Why do we need this info?</Fonts.Heading06>
                <Fonts.BodySmall style={{ marginTop: 12, marginBottom: 20 }}>
                    All of this information is required by law for us to create a medical record for your pet.
                    <br />
                    <br />
                    Modern Animal will never share your or your pet&apos;s data.
                </Fonts.BodySmall>
                <PrimaryButton color="black" onClick={() => setIsWhyInfoModalOpen(false)}>
                    Okay
                </PrimaryButton>
            </ModalContainer>
        </Modal>
    );

    const renderLoadingModal = () => {
        if (!loading || locationShowCreditCardStep) return null;
        return (
            <LoadingModal
                isVisible={loading}
                patientName={patient?.name}
            />
        );
    };

    return (
        <ClientInformationContainer>
            {renderClientInputBody()}
            {renderClientInputButton()}
            {renderWhyInfoModal()}
            {renderLoadingModal()}
        </ClientInformationContainer>
    );
};

export default ClientOnboarding;

const ClientInformationContainer = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: space-between;
    padding: 32px 24px 0 24px;
    gap: 8px;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const ClientInputContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
`;

const ModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin: 24px;
    gap: 8px;
    position: relative;
`;

const ModalExitContainer = styled.button`
    display: flex;
    position: absolute;
    top: 0;
    right: 0;
`;

const NextButtonContainer = styled.div<{ isMobile: boolean }>`
    display: flex;
    position: ${({ isMobile }) => (isMobile ? 'sticky' : 'static')};
    bottom: 0;
    background-color: ${colors.WHITE};
    padding: 16px 0 32px 0;
`;

const SectionContainer = styled(ClientInputContainer)`
    gap: 8px;
`;
